// "Pakete gösterge ekle" penceresi: iki sekme (Katalog · AI ile öner), sağda iki sekmenin
// birleşimini gösteren Seçim paneli (≤720 px tek sütun + alt özet şeridi). "Taslağa ekle" seçimi
// çağırana verir (kırılım kuralı + kaynak `addDraftItems`'ta); kalıcı yazım düzenleyicinin Kaydet'i.

import {
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type KeyboardEvent,
  type ReactNode,
  type SetStateAction,
} from "react";
import type { KpiMetric, KpiPackage, KpiPackageSuggestResponse } from "@mahir/shared";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import { autoAddCount } from "../../lib/kpi-package-add";
import { applySuggestRun } from "../../lib/kpi-package-suggest";
import type { DraftContext, PackagePick } from "../../lib/kpi-package-draft";
import { useMediaQuery } from "../../lib/use-media-query";
import { PackageAddCatalogTab } from "./PackageAddCatalogTab";
import { PackageSelectionPanel } from "./PackageSelectionPanel";
import { KpiModal } from "./KpiModal";
import { footerBtn, helpText } from "./kpi-form-styles";

const A = KPI_PACKAGES_COPY.add;
export type PackageAddTab = "catalog" | "suggest";

export interface PackageSuggestApi {
  picks: ReadonlyMap<string, PackagePick>;
  setPicks: Dispatch<SetStateAction<ReadonlyMap<string, PackagePick>>>;
  /** pencerenin dar (≤720 px) düzeni — sekme kendi yerleşimini buna göre kurar */
  narrow: boolean;
}

export interface PackageAddDialogProps {
  open: boolean;
  initialTab: PackageAddTab;
  pkg: KpiPackage;
  metrics: readonly KpiMetric[];
  ctx: DraftContext;
  /** taslaktaki üyeler (pasif + işaretli) */
  members: ReadonlySet<string>;
  /** AI sekmesi (Task 6) */
  suggestTab?: (api: PackageSuggestApi) => ReactNode;
  /**
   * Son biten AI koşumu (yoksa null). Seçim'e pencere düzeyinde BİR KEZ uygulanır — sekme
   * bağımsız: Katalog sekmesi açıkken biten koşum da Seçim'e girer (final review FE1).
   */
  suggestResult?: KpiPackageSuggestResponse | null;
  /** testte düzeni zorlamak için; verilmezse ≤720 px medya sorgusu */
  narrow?: boolean;
  onClose: () => void;
  onAdd: (picks: PackagePick[]) => void;
}

const TABS: readonly PackageAddTab[] = ["catalog", "suggest"];

/** Dar ekran özet şeridi: gövdenin altına yapışık (alt bilgi alanı), gövde içeriğinin üstünde. */
export const SUMMARY_STRIP: React.CSSProperties = {
  position: "sticky",
  bottom: 0,
  zIndex: 1,
  minWidth: 0,
  background: "var(--color-surface)",
  borderTop: "1px solid var(--color-border)",
  boxShadow: "0 -4px 12px rgba(0,0,0,0.06)",
};

export function PackageAddDialog(p: PackageAddDialogProps): JSX.Element | null {
  const { ctx, members } = p;
  const mq = useMediaQuery("(max-width: 720px)");
  const narrow = p.narrow ?? mq;
  const [tab, setTab] = useState<PackageAddTab>(p.initialTab);
  const [picks, setPicks] = useState<ReadonlyMap<string, PackagePick>>(() => new Map());

  useEffect(() => {
    if (p.open) {
      setTab(p.initialTab);
      setPicks(new Map());
    }
  }, [p.open, p.initialTab]);

  // Önceki render'dan bilgi saklama kalıbı (render sırasında güncelleme — effect değil): sonuç
  // aynı render'da Seçim'e girer, sekme yeniden bağlansa da ikinci kez uygulanmaz.
  const [appliedRun, setAppliedRun] = useState<KpiPackageSuggestResponse | null>(null);
  const run = p.open ? (p.suggestResult ?? null) : null;
  if (run !== null && run !== appliedRun) {
    setAppliedRun(run);
    setPicks((prev) => {
      const next = applySuggestRun([...prev.values()], run);
      return new Map(next.map((x) => [x.metricId, x]));
    });
  }

  const list = useMemo(() => [...picks.values()], [picks]);
  const auto = useMemo(() => autoAddCount(list, members, ctx), [list, members, ctx]);

  if (!p.open) return null;

  function onTabKey(e: KeyboardEvent<HTMLButtonElement>): void {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    e.preventDefault();
    const next = tab === "catalog" ? "suggest" : "catalog";
    setTab(next);
    requestAnimationFrame(() => document.getElementById(`pkg-add-tab-${next}`)?.focus());
  }

  const panel = (
    <PackageSelectionPanel
      picks={list}
      ctx={ctx}
      autoCount={auto}
      onRemove={(id) =>
        setPicks((prev) => {
          const out = new Map(prev);
          out.delete(id);
          return out;
        })
      }
    />
  );

  const footer = (
    <div className="flex flex-wrap items-center gap-2" style={{ justifyContent: "space-between" }}>
      <span style={{ ...helpText, flex: "1 1 220px" }}>{A.footerNote}</span>
      <span className="flex flex-wrap items-center gap-2">
        <button type="button" style={footerBtn()} onClick={p.onClose}>
          {A.cancel}
        </button>
        <button
          type="button"
          style={footerBtn(true)}
          disabled={list.length === 0}
          onClick={() => p.onAdd(list)}
        >
          {A.confirm(list.length)}
        </button>
      </span>
    </div>
  );

  return (
    <KpiModal
      open
      kicker={KPI_PACKAGES_COPY.page.kicker}
      title={A.title}
      description={A.description}
      onClose={p.onClose}
      dirty={list.length > 0}
      footer={footer}
      width={960}
    >
      <div
        className="grid gap-4"
        data-testid="package-add-body"
        style={{ gridTemplateColumns: narrow ? "minmax(0, 1fr)" : "minmax(0, 1fr) 270px" }}
      >
        <div style={{ minWidth: 0 }}>
          <div role="tablist" aria-label={A.tabsLabel} className="mb-3 flex gap-1">
            {TABS.map((t) => {
              const on = tab === t;
              return (
                <button
                  key={t}
                  id={`pkg-add-tab-${t}`}
                  type="button"
                  role="tab"
                  aria-selected={on}
                  aria-controls={`pkg-add-panel-${t}`}
                  tabIndex={on ? 0 : -1}
                  onClick={() => setTab(t)}
                  onKeyDown={onTabKey}
                  style={{
                    ...footerBtn(on),
                    padding: "8px 16px",
                    fontWeight: on ? 700 : 500,
                  }}
                >
                  {A.tabs[t]}
                </button>
              );
            })}
          </div>
          <div
            role="tabpanel"
            id={`pkg-add-panel-${tab}`}
            aria-labelledby={`pkg-add-tab-${tab}`}
            style={{ minWidth: 0 }}
          >
            {tab === "catalog" ? (
              <PackageAddCatalogTab
                metrics={p.metrics}
                ctx={ctx}
                members={members}
                picks={picks}
                setPicks={setPicks}
                narrow={narrow}
              />
            ) : (
              p.suggestTab?.({ picks, setPicks, narrow })
            )}
          </div>
        </div>
        {narrow ? (
          // ≤720 px: özet şeridi pencerenin alt bilgisine yapışık (gövde kaydırılırken görünür kalır);
          // açılınca panel ekranın en çok %40'ı kadar uzar ve kendi içinde kayar
          <div data-testid="package-add-summary" style={SUMMARY_STRIP}>
            <details>
              <summary className="text-sm font-bold" style={{ minHeight: 44, cursor: "pointer" }}>
                {A.summary(list.length)}
              </summary>
              <div style={{ maxHeight: "40vh", overflowY: "auto" }}>{panel}</div>
            </details>
          </div>
        ) : (
          <div
            style={{ borderLeft: "1px solid var(--color-border)", paddingLeft: 16, minWidth: 0 }}
          >
            {panel}
          </div>
        )}
      </div>
    </KpiModal>
  );
}
